import { ClickAway } from '@components/core-ui/ClickAway';
import { useEffect, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';

import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { FormFieldIcon } from '@components/core-ui/form-elements/FormFieldIcon';
import { FormFieldRoot } from '@components/core-ui/form-elements/FormFieldRoot';
import { FormFieldSelectLabel } from '@components/core-ui/form-elements/FormFieldSelectLabel';
import { FormFieldSelectOption } from '@components/core-ui/form-elements/FormFieldSelectOption';
import { FormFieldSelectOptionsPanel } from '@components/core-ui/form-elements/FormFieldSelectOptionsPanel';
import closedIcon from '@components/icons/ChevronClosed.svg';
import openedIcon from '@components/icons/ChevronOpened.svg';
import { TProps } from './types/TProps';

const autocompleteWrapperClassName = 'w-full [&>div]:relative';

const autocompleteScrollWrapperClassName = 'scrollWrapper block pt-10';

const autocompleteOptionsLabelClassName = [
  'optionsLabel absolute left-[15px] right-[15px] top-0 z-[100] flex h-10 cursor-pointer items-center',
  'border-b border-[var(--color-form-field-select-divider,var(--color-border))]',
].join(' ');

const autocompleteFilterInputClassName = [
  "h-5 w-full cursor-text border-none bg-transparent p-0 font-['General_Sans',sans-serif] text-xs font-medium leading-3 outline-none",
  '!text-[var(--color-form-field-text,var(--color-text-muted))]',
  'placeholder:text-[var(--color-form-field-placeholder,var(--color-text-muted))] placeholder:opacity-100 placeholder:font-medium',
].join(' ');

const autocompleteOptionsClassName = 'options block';

const autocompleteIconClosedClassName = 'icon--closed right-5 h-auto w-auto cursor-pointer';

const autocompleteIconOpenedClassName = 'icon--opened right-[5px] h-auto w-auto cursor-pointer';

export const AutoCompleteSelect = ({
  name,
  options,
  label,
  onChange,
  value = '',
  className = 'form-autocomplete',
  hideSelectedOption = false,
  required = false,
  disabled = false,
  error = false,
  customOpenIcon: OpenIcon = openedIcon,
  customCloseIcon: CloseIcon = closedIcon,
  placement = 'bottom-start',
}: TProps) => {
  const [optionsVisible, setOptionsVisible] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const filterInputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const toggleOptions = () => {
    if (disabled) {
      return;
    }
    setOptionsVisible(!optionsVisible);
    setFilteredOptions(options);
  };

  useEffect(() => {
    if (optionsVisible) {
      filterInputRef?.current?.focus();
    }
  }, [optionsVisible]);

  const closeOptions = () => {
    setOptionsVisible(false);
  };

  const setFilter = (value: string) => {
    setFilteredOptions(options.filter((option) => option.label.toLowerCase().includes(value.toLowerCase())));
    setSelectOptions();
  };

  let selectOptions: string | React.ReactNode[] = [];
  const setSelectOptions = () => {
    selectOptions = filteredOptions
      .filter((option) => (hideSelectedOption ? option.value !== value : true))
      .map((option, k) => {
        const onClick = () => {
          onChange(name, `${option.value}`);
          setOptionsVisible(!optionsVisible);
        };
        return (
          <FormFieldSelectOption onClick={onClick} selected={`${value}` === `${option.value}`} key={k}>
            {option.label}
          </FormFieldSelectOption>
        );
      });
  };
  setSelectOptions();

  const selectedOption = options.find((option) => {
    return `${option.value}` === `${value}`;
  });

  const getCurrentLabel = () => {
    if (selectedOption) {
      return selectedOption.label;
    }
    return `${label}${required ? ' *' : ''}`;
  };

  return (
    <FormFieldRoot disabled={disabled} className={[autocompleteWrapperClassName, className].filter(Boolean).join(' ')}>
      <ClickAway onClickAway={closeOptions}>
        <span
          ref={triggerRef}
          className={[
            'select select-control',
            disabled ? 'select-control--disabled' : '',
            error ? 'select-control--error' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={toggleOptions}
        >
          <FormFieldIcon clickable className={autocompleteIconClosedClassName}>
            <IconButtonClean
              type="button"
              aria-label="Open options"
              onClick={(e) => {
                e.stopPropagation();
                toggleOptions();
              }}
            >
              <CloseIcon />
            </IconButtonClean>
          </FormFieldIcon>
          <FormFieldSelectLabel selected={Boolean(selectedOption)} disabled={disabled}>
            {getCurrentLabel()}
          </FormFieldSelectLabel>
        </span>

        {optionsVisible ? (
          <FormFieldSelectOptionsPanel triggerRef={triggerRef} placement={placement}>
            <span className={autocompleteOptionsLabelClassName}>
              <input
                ref={filterInputRef}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={getCurrentLabel()}
                className={autocompleteFilterInputClassName}
              />
              <FormFieldIcon clickable className={autocompleteIconOpenedClassName}>
                <IconButtonClean
                  type="button"
                  aria-label="Close options"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOptions();
                  }}
                >
                  <OpenIcon />
                </IconButtonClean>
              </FormFieldIcon>
            </span>
            <span className={autocompleteScrollWrapperClassName}>
              <Scrollbars autoHeight autoHeightMin={24} autoHeightMax={150}>
                <span className={autocompleteOptionsClassName}>{selectOptions}</span>
              </Scrollbars>
            </span>
          </FormFieldSelectOptionsPanel>
        ) : null}
      </ClickAway>
    </FormFieldRoot>
  );
};
